import { AxiosRequestConfig } from 'axios';
import {
  CreateReminderDto,
  DocumentFileDto,
  DocumentFileResponseDto,
  GetProfileMedicationsDto,
  GetProfileTreatmentDto,
  GetReminderDto,
  UpdateReminderDto,
} from 'woofwoof-api';
import { axiosBackendClient } from './client';

/**
 * Visit-scoped prescribing endpoints.
 *
 * These hit the endpoints that branch server-side on `visit.profileId`:
 *   - authed visit  → entity lands in the collection with profileId set
 *   - guest visit   → entity lands in the collection with profileId = null
 *
 * The FE doesn't need to know which branch is taken — just pass the visitId.
 * List responses are normalized server-side to the same DTO shapes used by the
 * profile-scoped endpoints.
 *
 * Medication and treatment creates and edits are clinical write series
 * (R3-4) and go through the generated clients instead
 * (`useMedicationWriteSeries`, `useTreatmentWriteSeries`).
 */

const base = (visitId: string) =>
  `/api/organization-profile/visit/${visitId}`;

export const visitScopedPrescribingApi = {
  // --- Medications ---
  deleteMedication: (visitId: string, medicationId: string) =>
    axiosBackendClient.delete(`${base(visitId)}/medication/${medicationId}`),

  // --- Treatments ---
  deleteTreatment: (visitId: string, treatmentRecordId: string) =>
    axiosBackendClient.delete(
      `${base(visitId)}/treatment/${treatmentRecordId}`,
    ),

  // --- Reminders ---
  addReminder: (visitId: string, dto: CreateReminderDto) =>
    axiosBackendClient.post(`${base(visitId)}/reminder`, dto),

  updateReminder: (
    visitId: string,
    reminderId: string,
    dto: UpdateReminderDto,
  ) =>
    axiosBackendClient.put(`${base(visitId)}/reminder/${reminderId}`, dto),

  deleteReminder: (visitId: string, reminderId: string) =>
    axiosBackendClient.delete(`${base(visitId)}/reminder/${reminderId}`),

  // --- Documents ---
  addDocuments: (visitId: string, files: DocumentFileDto[]) =>
    axiosBackendClient.post(`/api/visit/${visitId}/documents`, files),

  // --- Lists (used by FE edit-form pre-fill on guest visits and the visit
  // modal's per-entity tabs) ---
  // `options`: the read's organization and signal (`useClinicalQuery`).
  listMedications: (visitId: string, options?: AxiosRequestConfig) =>
    axiosBackendClient.get<GetProfileMedicationsDto[]>(
      `${base(visitId)}/medications`,
      options,
    ),

  listTreatments: (visitId: string, options?: AxiosRequestConfig) =>
    axiosBackendClient.get<GetProfileTreatmentDto[]>(
      `${base(visitId)}/treatments`,
      options,
    ),

  listReminders: (visitId: string) =>
    axiosBackendClient.get<GetReminderDto[]>(`${base(visitId)}/reminders`),

  listDocuments: (visitId: string) =>
    axiosBackendClient.get<DocumentFileResponseDto[]>(
      `/api/visit/${visitId}/documents`,
    ),

  deleteDocument: (visitId: string, filename: string) =>
    axiosBackendClient.delete<DocumentFileResponseDto[]>(
      `/api/visit/${visitId}/documents`,
      { params: { filename } },
    ),
};
